/*
 * Correlation-id generation for DeckorateAPI requests.
 *
 * Every DeckorateAPI endpoint takes a `request_id` that is echoed back so a
 * request and its response can be tied together in logs. Five feature clients
 * (SpeakerNotes, FixAlignment, Infographic, SketchToSlide, PhotoHeadshot) each
 * carry their own copy of the logic below; this is the shared version, so
 * clients added from here on don't make it six. Those five can adopt it as a
 * separate change — the behaviour here is deliberately identical to theirs.
 */

/**
 * request_id is a non-secret correlation id echoed back for request/response
 * tracing in logs — it is never used for auth or any other security decision.
 *
 * Three strategies are tried in order, for browser compatibility:
 * `randomUUID()` is preferred; `getRandomValues()` covers browsers with Web
 * Crypto but no `randomUUID()`; and, only when Web Crypto is entirely
 * unavailable, a timestamp plus a monotonic counter as a last resort.
 *
 * That last fallback is NOT cryptographically secure and must never be used for
 * anything beyond a best-effort, non-secret correlation id. Since the id needs
 * no randomness at all, it deliberately avoids `Math.random()` too.
 */

/**
 * Counts calls that fell through to the last-resort id below. Module-scoped
 * (not per-request) purely so consecutive fallback ids stay unique for the
 * lifetime of this page/tab; it has no meaning outside that.
 */
let fallbackRequestSequence = 0;

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * A correlation id for one DeckorateAPI call.
 *
 * @param prefix Prepended to the non-`randomUUID` forms so an id in a log can
 *   be traced to the feature that produced it (e.g. `"classify-image-"`).
 *   `randomUUID()` output is left unprefixed, matching the existing clients.
 */
export function newRequestId(prefix: string): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  if (cryptoObj?.getRandomValues) {
    return `${prefix}${bytesToHex(cryptoObj.getRandomValues(new Uint8Array(16)))}`;
  }
  fallbackRequestSequence += 1;
  return `${prefix}${Date.now().toString(36)}-${fallbackRequestSequence}`;
}
